"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import Footer from "@/components/footer";
import TurnstileChallenge from "@/components/turnstile-challenge";
import { buildAuthFlowRedirectUrl, safeAccountDestination } from "@/lib/auth/redirect-target";
import { getAuthErrorMessage } from "@/lib/auth/error-message";
import { captureProductEvent } from "@/lib/posthog/product-events";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { buildPublicSiteUrl } from "@/lib/site-url";

const providerButtons = [
  {
    id: "google",
    label: "Google",
    icon: "/icons/oauth/google.svg",
  },
  {
    id: "github",
    label: "GitHub",
    icon: "/icons/oauth/github.svg",
  },
] as const;

type OAuthProvider = (typeof providerButtons)[number]["id"];

type SignInPageClientProps = {
  initialDestination?: string | null;
  initialCode: string | null;
  initialType: string | null;
  initialStatus: string | null;
  initialErrorMessage: string | null;
};

export default function SignInPageClient({
  initialDestination,
  initialCode,
  initialType,
  initialStatus,
  initialErrorMessage,
}: SignInPageClientProps) {
  const supabase = getSupabaseBrowserClient();
  const router = useRouter();
  const destination = safeAccountDestination(initialDestination);
  const hasExchanged = useRef(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [magicEmail, setMagicEmail] = useState("");
  const [activeAction, setActiveAction] = useState<
    "password" | "magic" | "exchange" | "oauth" | null
  >(initialCode ? "exchange" : null);
  const [status, setStatus] = useState<string | null>(initialStatus);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    initialErrorMessage,
  );
  const [passwordCaptchaToken, setPasswordCaptchaToken] = useState<string | null>(null);
  const [magicCaptchaToken, setMagicCaptchaToken] = useState<string | null>(null);
  const [passwordCaptchaResetKey, setPasswordCaptchaResetKey] = useState(0);
  const [magicCaptchaResetKey, setMagicCaptchaResetKey] = useState(0);

  useEffect(() => {
    if (hasExchanged.current || !initialCode) {
      return;
    }

    hasExchanged.current = true;
    supabase.auth
      .initialize()
      .then(async ({ error: initializationError }) => {
        // Supabase retains an old session when a callback fails. Honor that
        // failure before considering any session already stored in this browser.
        if (initializationError) return { error: initializationError };
        // Successful automatic PKCE exchange removes the code from the URL.
        // A remaining code still needs its own exchange, even with an old session.
        if (!new URL(window.location.href).searchParams.has("code")) {
          const { data } = await supabase.auth.getSession();
          if (data.session) return { error: null };
        }
        const { error } = await supabase.auth.exchangeCodeForSession(initialCode);
        return { error };
      })
      .then(({ error }) => {
        if (error) {
          setErrorMessage(getAuthErrorMessage(error));
          setStatus(null);
          return;
        }
        if (initialType === "magiclink") {
          supabase.auth
            .getUser()
            .then(({ data, error: userError }) => {
              if (!userError && data?.user?.user_metadata) {
                return supabase.auth.updateUser({
                  data: {
                    ...data.user.user_metadata,
                    has_magic_link: true,
                  },
                });
              }
              return null;
            })
            .catch(() => null);
        }
        setStatus("Signed in successfully. Redirecting...");
        router.replace(destination);
      })
      .catch(() => {
        setErrorMessage("We could not finish sign-in. Check your connection, then sign in or request a new link below.");
        setStatus(null);
      })
      .finally(() => setActiveAction(null));
  }, [initialCode, initialType, router, supabase, destination]);

  useEffect(() => {
    if (initialCode || initialErrorMessage) {
      return;
    }

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (data.session) {
          if (initialType === "magiclink") {
            supabase.auth
              .getUser()
              .then(({ data: userData, error: userError }) => {
                if (!userError && userData?.user?.user_metadata) {
                  return supabase.auth.updateUser({
                    data: {
                      ...userData.user.user_metadata,
                      has_magic_link: true,
                    },
                  });
                }
                return null;
              })
              .catch(() => null);
          }
          router.replace(destination);
        }
      })
      .catch(() => null);
  }, [initialCode, initialType, initialErrorMessage, router, supabase, destination]);

  const handlePasswordSignIn = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!passwordCaptchaToken) {
      setErrorMessage("Complete human verification before continuing.");
      return;
    }
    setActiveAction("password");
    setErrorMessage(null);
    setStatus(null);
    captureProductEvent("signin_started", {
      method: "password",
      surface: "website",
    });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken: passwordCaptchaToken },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        return;
      }

      setStatus("Signed in successfully. Redirecting...");
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (!userError && userData?.user?.user_metadata) {
          const nextMetadata = {
            ...userData.user.user_metadata,
            has_password: true,
          };
          await supabase.auth.updateUser({ data: nextMetadata });
        }
      } catch {
        // Metadata sync is best-effort after successful sign-in.
      }
      router.push(data?.weakPassword ? "/account/settings?weak_password=1" : destination);
    } catch {
      setErrorMessage("We could not connect. Check your connection and try again. Your details are still here.");
      setStatus(null);
    } finally {
      setActiveAction(null);
      setPasswordCaptchaToken(null);
      setPasswordCaptchaResetKey((current) => current + 1);
    }
  };

  const handleMagicLink = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!magicCaptchaToken) {
      setErrorMessage("Complete human verification before continuing.");
      return;
    }
    setActiveAction("magic");
    setErrorMessage(null);
    setStatus(null);
    captureProductEvent("signin_started", {
      method: "magic",
      surface: "website",
    });

    try {
      const redirectTo = buildAuthFlowRedirectUrl(
        destination,
        "magic_signin",
        window.location.origin,
      );
      const { error } = await supabase.auth.signInWithOtp({
        email: magicEmail,
        options: {
          captchaToken: magicCaptchaToken,
          emailRedirectTo: redirectTo,
          shouldCreateUser: false,
        },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        return;
      }

      setStatus(`Check your inbox at ${magicEmail.trim()}. Open the sign-in link in this browser. Check spam if it has not arrived, or request another link below.`);
    } catch {
      setErrorMessage("We could not connect. Check your connection and try again. Your details are still here.");
      setStatus(null);
    } finally {
      setActiveAction(null);
      setMagicCaptchaToken(null);
      setMagicCaptchaResetKey((current) => current + 1);
    }
  };

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setActiveAction("oauth");
    setErrorMessage(null);
    setStatus("Redirecting to your provider...");
    captureProductEvent("signin_started", {
      method: `oauth_${provider}`,
      surface: "website",
    });

    try {
      const redirectTo = buildPublicSiteUrl(
        `/auth/callback?next=${encodeURIComponent(destination)}`,
        window.location.origin,
      );
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
        },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        setStatus(null);
        setActiveAction(null);
      }
    } catch {
      setErrorMessage("We could not open your sign-in provider. Check your connection and try again.");
      setStatus(null);
      setActiveAction(null);
    }
  };

  return (
    <div className="min-h-screen text-[var(--text)]">
      <main className="relative min-h-screen">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 pb-24 pt-12 lg:flex-row lg:items-start">
          <section className="flex-1 space-y-6">
            <Link
              href="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-[var(--text)] transition hover:border-white/40"
            >
              Return home
            </Link>
            <div className="space-y-3">
              <p className="text-sm uppercase tracking-[0.34em] text-white/80">
                deepface.dev access
              </p>
              <h1 className="font-[var(--font-display)] text-3xl leading-tight text-[var(--text)] sm:text-4xl">
                Sign in to your account
              </h1>
            </div>
            <div className="panel border border-white/15 bg-white/[0.03] p-6">
              <p className="text-sm uppercase tracking-[0.24em] text-white/80">
                New here?
              </p>
              <p className="mt-3 text-sm leading-7 text-white/90">
                Create your account for 50 free API calls every month, with no
                card required. We will guide you from a sample result to your own
                images and application.
              </p>
              <Link
                href={`/signup?next=${encodeURIComponent(destination==="/account"?"/account/keys":destination)}`}
                className="mt-5 inline-flex rounded-full border border-white/30 bg-white/10 px-6 py-2 text-sm font-semibold text-[var(--text)] transition hover:border-white/50"
              >
                Create account
              </Link>
            </div>
          </section>

          <section className="panel w-full max-w-xl border border-white/15 p-6 sm:p-8">
            <div className="space-y-5">
              <p className="text-sm uppercase tracking-[0.28em] text-white/80">
                Sign in options
              </p>

              {(status || errorMessage) && (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm leading-6 ${
                    errorMessage
                      ? "border-red-400/40 text-red-200"
                      : "border-emerald-400/40 text-emerald-200"
                  }`}
                  role={errorMessage ? "alert" : "status"}
                >
                  {errorMessage ?? status}
                </div>
              )}
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2">
                  {providerButtons.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      onClick={() => handleOAuthSignIn(provider.id)}
                      disabled={activeAction !== null}
                      aria-label={`Continue with ${provider.label}`}
                      className="flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/[0.12] px-3 py-2.5 sm:px-4 text-base font-semibold text-[var(--text)] transition hover:border-white/50 hover:bg-white/[0.16] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/60 bg-white/95 shadow-[0_4px_12px_rgba(2,6,23,0.35)]">
                        <Image
                          src={provider.icon}
                          alt=""
                          width={24}
                          height={24}
                          suppressHydrationWarning
                          className="h-5 w-5"
                        />
                      </span>
                      <span>{provider.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-white/70">
                <span className="h-px flex-1 bg-white/20" aria-hidden="true" />
                or
                <span className="h-px flex-1 bg-white/20" aria-hidden="true" />
              </div>

              <form
                method="post"
                className="space-y-4 rounded-2xl border border-white/20 bg-white/[0.08] p-5"
                onSubmit={handlePasswordSignIn}
              >
                <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                  Email
                  <input
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                  Password
                  <input
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                  />
                </label>
                <button
                  type="submit"
                  disabled={activeAction !== null || !passwordCaptchaToken}
                  className="w-full rounded-xl bg-[var(--accent)] px-6 py-3 text-base font-semibold text-black transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {activeAction === "password"
                    ? "Signing in..."
                    : "Sign in with email"}
                </button>
                <TurnstileChallenge
                  key={passwordCaptchaResetKey}
                  action="signin_password"
                  onTokenChange={setPasswordCaptchaToken}
                />
                <p className="text-sm text-white/75">
                  Forgot your password?{" "}
                  <Link
                    href="/reset-password"
                    className="text-[var(--accent)] hover:text-[var(--accent-2)]"
                  >
                    Reset it
                  </Link>
                  .
                </p>
              </form>

              <div className="rounded-2xl border border-white/20 bg-white/[0.08] p-5">
                <form
                  method="post"
                  className="space-y-3"
                  onSubmit={handleMagicLink}
                >
                  <p className="text-sm uppercase tracking-[0.2em] text-white/80">
                    Magic link
                  </p>
                  <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                    Email
                    <input
                      type="email"
                      name="magic-email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      value={magicEmail}
                      onChange={(event) => setMagicEmail(event.target.value)}
                      required
                      className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={activeAction !== null || !magicCaptchaToken}
                    className="w-full rounded-xl border border-white/30 bg-white/10 px-6 py-3 text-base font-semibold text-[var(--text)] transition hover:border-white/50 hover:bg-white/[0.14] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {activeAction === "magic"
                      ? "Sending link..."
                      : "Send magic link"}
                  </button>
                  <TurnstileChallenge
                    key={magicCaptchaResetKey}
                    action="signin_magic"
                    onTokenChange={setMagicCaptchaToken}
                  />
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer showAuthCtas={false} />
    </div>
  );
}
